import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {Box,Button,Link,Pagination,IconButton} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import backarrow from '../../../assets/images/backarrow.svg';
import AppTable from '../../app-table/AppTable';
import SuccessDialog from '../../dialog-box/SuccessDialog';
import ConfirmDeleteDialogBox from '../../dialog-box/ConfirmDeleteDialogBox';
import { AppChip } from '../../StyledComponent';
import StatusStepper from './components/StatusStepper';
import ClientDetailsDrawer from './components/ClientDetailsDrawer';
import OnboardClientModal from './OnboardClientModal';
import { useDeleteClientById, useGetClientsById } from '../../../hooks/useClientOnboarding';
import { useFamilyListMutation } from '../../../hooks/useCommon';
import { useProductById } from '../../../hooks/useProduct';
import {TaskProgressContainer, ConnectorLineRight, ConnectorLineLeft, StatusStepperWrapper, StatusChipContainer} from './components/styledComponents';

const PAGE_SIZE = 10;
const statusOptions = [
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'To Do', value: 'TODO' },
];

const getStatusConfig = (status) => {
  switch (status) {
    case 'Completed':
      return {
        backgroundColor: '#E8F5E9',
        textColor: '#2E7D32',
        label: 'Completed',
      };
    case 'Inprogress':
      return {
        backgroundColor: '#FFF3E0',
        textColor: '#E65100',
        label: 'In Progress',
      };
    default:
      return {
        backgroundColor: '#F5F5F5',
        textColor: '#616161',
        label: 'To Do',
      };
  }
};

const ProductClientDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isOnboardModalOpen, setIsOnboardModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [filters, setFilters] = useState({ status: 'all', page: 1, pageSize: PAGE_SIZE });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerParams, setDrawerParams] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const { data: productData, refetch } = useGetClientsById(id, filters);
  const { data: productDetails, isLoading: isLoadingProductDetails } = useProductById(id);
  const { mutateAsync: fetchFamilyList, isLoading: isFamilyListLoading, data: familyList } = useFamilyListMutation();
  const taskColumnsData = productData?.data?.[0]?.taskStatuses || productDetails?.taskStatuses || [];
  
  // Get product name - prioritize from client data, fallback to product details
  const productName = productData?.data?.[0]?.productName || productDetails?.productName || "";
  
  const { mutateAsync: deleteClientById, isLoading: isDeleteClientByIdLoading } = useDeleteClientById();
  const handleOnboardClientClick = async () => {
    try {
      await fetchFamilyList();
      setIsOnboardModalOpen(true);
    } catch (error) {
      console.error('Error fetching family list:', error);
    }
  };

  const handleOnboardClient = () => {
    setIsOnboardModalOpen(false);
    setIsSuccessModalOpen(true);
    refetch();
  };

  const handleFilterChange = (input) => {
    const { name, value } = input.target ? input.target : input;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (value) => {
    setFilters((prev) => ({ ...prev, page: value }));
  };

  const handleFamilyNameClick = (row) => {
    console.log("row",row);
    setDrawerParams({
      customerClientNameId: row.customerClientNameId,
      productId: row.productId,
      customerId: row.customerId,
    });
    setIsDrawerOpen(true);
  }

  const handleEditClick = (row) => {
    navigate(`/products/task-details/${row.id}`);
  };

  const handleDeleteClick = (row) => {
    setDeleteTargetId(row.id);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteClientById(deleteTargetId);
      setIsDeleteDialogOpen(false);
      setDeleteTargetId(null);
      await refetch();
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const getColumns = () => {
    const staticColumns = [
      {
        label : 'S.No',
        field: 'sNo',
        width: 50,
        align: 'center',
        render: (row, index) => index + 1 + (filters.page - 1) * filters.pageSize,  
      },

      {
        label: 'Family Name',
        field: 'familyName',
        sort: true,
        width: 200,
        render: (row) => (
          <Link
            component="button"
            onClick={() => handleFamilyNameClick(row)}
            sx={(theme) => ({
              color: theme.palette.custom.brown.main,
              textDecoration: 'none',
              textAlign: 'left',
              '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
            })}
          >
            {row.familyName} - {row.clientName}
          </Link>
        ),
      },
    ];

    const taskColumns = taskColumnsData.map((task, taskIndex) => ({
      label: task.taskName,
      field: `taskStatuses.${task.taskId}`,
      align: 'left',
      width: 180,
      render: (row) => {
        const taskStatus = row.taskStatuses?.find((t) => t.taskId === task.taskId)?.status || 'TODO';
        
        return (
          <TaskProgressContainer>
            {taskIndex < taskColumnsData.length - 1 && <ConnectorLineRight />}            
            {taskIndex > 0 && <ConnectorLineLeft />}           
            <StatusStepperWrapper>
              <StatusStepper status={taskStatus} />
            </StatusStepperWrapper>
          </TaskProgressContainer>
        );
      },
    }));

    const endColumns = [
      {
        label: 'Status',
        field: 'overallStatus',
        align: 'center',
        width: 150,
        render: (row) => {
          const config = getStatusConfig(row.overallStatus);
          return (
              <AppChip
                label={
                  <StatusChipContainer>
                    <FiberManualRecordIcon sx={{ fontSize: 8, color: config.textColor }} />
                    {config.label}
                  </StatusChipContainer>
                }
                backgroundColor={config.backgroundColor}
                textColor={config.textColor}
                fontSize="0.875rem"
                sx={{ 
                  minWidth: '150px',
                  justifyContent: 'center'
                }}
              />
          );
        },
      },
      {
        label: 'Action',
        field: 'action',
        align: 'center',
        width: 60,
        render: (row) => (
          <>
            {row.overallStatus === 'Completed' ? (
              <VisibilityIcon
                sx={(theme) => ({
                  color: theme.palette.custom.brown.main,
                  cursor: 'pointer',
                  '&:hover': { color: theme.palette.custom.brown.medium },
                })}
                onClick={() => handleEditClick(row)}
              />
            ) : (
              <EditIcon
                sx={(theme) => ({
                  color: theme.palette.custom.brown.main,
                  cursor: 'pointer',
                  '&:hover': { color: theme.palette.custom.brown.medium },
                })}
                onClick={() => handleEditClick(row)}
              />
            )}
            <DeleteIcon
              sx={(theme) => ({
                color: row.overallStatus === 'TODO' 
                  ? theme.palette.custom.brown.main 
                  : theme.palette.action.disabled,
                cursor: row.overallStatus === 'TODO' ? 'pointer' : 'default',
                ml: 1,
                '&:hover': { 
                  color: row.overallStatus === 'TODO' 
                    ? theme.palette.custom.brown.medium 
                    : theme.palette.action.disabled 
                },
              })}
              onClick={row.overallStatus === 'TODO' ? () => handleDeleteClick(row) : undefined}
            />
          </>
        ),
      },
    ];
    return [...staticColumns, ...taskColumns, ...endColumns];
  };

  const dynamicFilters = [
    {
      type: 'dropdown',
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { label: 'All', value: 'all' },
        ...statusOptions,
      ],
      disableClearable: true,
    },
  ];

  const totalCount = productData?.pagination?.totalCount || 0;
  const totalPages = productData?.pagination?.totalPages || 1;
  const tableData = productData?.data || [];
  const shouldShowSpinner = isLoadingProductDetails && (!productData?.data || productData.data.length === 0);

  return (
    <>
      <AppTable
        spinner={shouldShowSpinner}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/products")}
            >  
              <img src={backarrow} alt="Back" style={{ width: 30, height: 30 }} />
            </IconButton>
              {productName}
          </Box>
        }
        columns={getColumns()}
        isOnboardClient={true}
        handleOnboardClientClick={handleOnboardClientClick}
        isSearch={true}
        searchPlaceholder="Search Client Name"
        handleFilterChange={handleFilterChange}
        dynamicFilters={dynamicFilters}
        tableData={{
          data: tableData,
          page: filters.page,
          totalCount: totalCount,
          totalPages: totalPages,
        }}
        filterState={filters}
        pageSize={PAGE_SIZE}
        renderPageNavigator={() => (
          <Pagination
            className="pagination-number"
            count={totalPages}
            page={filters.page}
            onChange={(e, value) => handlePageChange(value)}
            color="primary"
          />
        )}
      />
      <OnboardClientModal
        open={isOnboardModalOpen}
        onClose={() => setIsOnboardModalOpen(false)}
        onSubmit={handleOnboardClient}
        productId={id}
        familyList={familyList?.data}
        isLoading={isFamilyListLoading}
      />
      <SuccessDialog
        open={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        title="Success!"
      >
        Client onboarded successfully.
      </SuccessDialog>
      <ClientDetailsDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        params={drawerParams}
      />
      <ConfirmDeleteDialogBox
        isConfirmOpen={isDeleteDialogOpen}
        handleConfirmClose={handleCloseDeleteDialog}
        handleDeleteHoliday={handleConfirmDelete}
      />
    </>
  );
};

export default ProductClientDetails;